import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Mail,
  Lock,
  Users,
  ShieldCheck,
  Crown,
  CreditCard,
  ExternalLink,
  AlertTriangle,
  Loader2,
  Check,
  ScrollText,
  Globe,
  Activity,
  Download,
} from 'lucide-react';



import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useT } from '@/contexts/LanguageContext';
import { Logo } from '@/components/Header';
import LanguageSelector from '@/components/LanguageSelector';
import EmailPreferences from '@/components/EmailPreferences';
import { exportFamilyTreeBackup } from '@/lib/familyBackupExport';
import DataProtectionPanel from '@/components/DataProtectionPanel';
import MembershipOperationsCenter from '@/components/membership/MembershipOperationsCenter';
import InviteModal from '@/components/InviteModal';
import type {
  MembershipInviteType,
} from '@/governor/membership/MembershipInviteLifecycle';



interface SubscriptionInfo {
  id: string;
  status: string;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  canceled_at: number | null;
  amount: number | null;
  currency: string | null;
  interval: string | null;
  interval_count: number | null;
  price_id: string | null;
}
const planLabel = (plan: string) =>
  (plan === 'annual_founding' || plan === 'annual_standard')
    ? 'Founding Member (Annual)'
    : plan === 'monthly_premium'
    ? 'Premium (Monthly)'
    : 'Free';

const fmtMoney = (cents: number | null, currency: string | null) => {
  if (cents == null) return '—';
  const cur = (currency || 'usd').toUpperCase();
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: cur,
  }).format(cents / 100);
};

const fmtDate = (unix: number | null) => {
  if (!unix) return '—';
  return new Date(unix * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// ─────────────────────────────────────────────────────────────────────────
// Billing-cycle aware status resolver.
//
//   • Monthly + trialing → "Trial ends in X days" (the only place we ever
//     show trial UI). current_period_end equals trial_end while a Stripe
//     subscription is in the trialing state, so we use it as the trial end.
//
//   • Yearly + active   → "Renews on [date]" (never trialing — the edge
//     function refuses to attach a trial to a yearly sub).
//
//   • Yearly + trialing → defensive fallback. This should never happen now
//     that monthly and yearly are strictly separated, but if a stale row
//     ever shows up we treat it as active and only show the renewal date.
//     We deliberately do NOT show the "Trial ends" line for yearly under
//     any circumstance.
// ─────────────────────────────────────────────────────────────────────────
type BillingDisplayMode = 'trial' | 'renews' | 'ends' | 'inactive';

interface BillingDisplay {
  mode: BillingDisplayMode;
  // For 'trial': days remaining (>= 0). For 'renews'/'ends': formatted date string.
  primary: string;
  // Optional secondary line, e.g. "$4.99 / month" or "Auto-billed after trial".
  secondary?: string;
}

function resolveBillingDisplay(
  sub: {
    status: string;
    interval: string | null;
    current_period_end: number | null;
    cancel_at_period_end: boolean;
    amount: number | null;
    currency: string | null;
  } | null
): BillingDisplay | null {
  if (!sub) return null;
  const isMonthly = sub.interval === 'month';
  const isYearly = sub.interval === 'year';
  const cancelling = sub.cancel_at_period_end;

  // Monthly subscription in trial → show "Trial ends in X days".
  if (isMonthly && sub.status === 'trialing' && sub.current_period_end) {
    const now = Date.now();
    const endMs = sub.current_period_end * 1000;
    const daysLeft = Math.max(0, Math.ceil((endMs - now) / (1000 * 60 * 60 * 24)));
    return {
      mode: 'trial',
      primary:
        daysLeft <= 0
          ? 'Trial ends today'
          : daysLeft === 1
          ? 'Trial ends in 1 day'
          : `Trial ends in ${daysLeft} days`,
      secondary: cancelling
        ? 'No charge — cancellation scheduled'
        : 'Auto-billed $4.99/mo when the trial ends',
    };
  }

  // Cancellation scheduled → show end-of-period date.
  if (cancelling && sub.current_period_end) {
    return {
      mode: 'ends',
      primary: `Ends on ${fmtDate(sub.current_period_end)}`,
      secondary:
        isMonthly ? 'Cancels at the end of the current month' : 'Cancels at the end of the current year',
    };
  }

  // Active (or past_due / unpaid) → show next renewal date.
  if (sub.current_period_end) {
    return {
      mode: 'renews',
      primary: `Renews on ${fmtDate(sub.current_period_end)}`,
      secondary: isYearly
        ? 'Yearly Founder\'s plan · no trial'
        : isMonthly
        ? 'Monthly plan · cancel anytime'
        : undefined,
    };
  }

  return { mode: 'inactive', primary: '—' };
}


const Account: React.FC = () => {
  const navigate = useNavigate();
  const t = useT();
  const {
    user,
    profile,
    plan,
    isAuthenticated,
    isOwner,
    isAdmin,
    loading,
    refreshProfile,
    updateProfile,
    signOut,
  } = useAuth();


  // Form state
  const [name, setName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  // Saving flags
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Subscription state
  const [sub, setSub] = useState<SubscriptionInfo | null>(null);
  const [subLoading, setSubLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  // Delete state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Backup export state (owner/admin only)
  const [exporting, setExporting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteMode, setInviteMode] =
    useState<MembershipInviteType>('FAMILY_PROFILE');
  const [profileClaimed, setProfileClaimed] = useState(false);


  // Hydrate form fields from profile
  useEffect(() => {
    setName(profile?.name ?? '');
    setFamilyName(profile?.family_name ?? '');
    setEmail(user?.email ?? profile?.email ?? '');
  }, [profile?.name, profile?.family_name, profile?.email, user?.email]);

  // Fetch subscription details if we have a stripe customer
  const loadSubscription = useCallback(async () => {
    const customerId = profile?.stripe_customer_id;
    if (!customerId) {
      setSub(null);
      return;
    }
    setSubLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('account-management', {
        body: { action: 'subscription-status', customerId },
      });
      if (error) {
        console.error('[Account] subscription-status error', error);
        setSub(null);
      } else {
        setSub(data?.subscription ?? null);
      }
    } catch (err) {
      console.error('[Account] subscription-status exception', err);
      setSub(null);
    } finally {
      setSubLoading(false);
    }
  }, [profile?.stripe_customer_id]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    let cancelled = false;

    const loadClaimState = async () => {
      if (!user?.id) {
        setProfileClaimed(false);
        return;
      }

      const { data, error } = await supabase
        .from('family_graph_members')
        .select('id')
        .eq('claimed_by_user_id', user.id)
        .is('deleted_at', null)
        .limit(1);

      if (cancelled) return;

      if (error) {
        console.error(
          '[Account] profile claim state failed',
          error,
        );
        setProfileClaimed(false);
        return;
      }

      setProfileClaimed(
        Array.isArray(data) && data.length > 0,
      );
    };

    void loadClaimState();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Save name + family name
  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateProfile({
        name: name.trim() || null,
        family_name: familyName.trim() || null,
      });
      toast.success('Profile updated');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // Save email (auth.updateUser)
  const handleSaveEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    if (trimmed === user?.email) {
      toast('That is already your email.');
      return;
    }
    setSavingEmail(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: trimmed });
      if (error) {
        toast.error(error.message || 'Failed to update email');
        return;
      }
      // Mirror onto profile row too (best-effort)
      try {
        if (user?.id) {
          await supabase.from('profiles').update({ email: trimmed }).eq('id', user.id);
        }
      } catch {
        /* non-fatal */
      }
      await refreshProfile();
      toast.success(
        'Email update started. Check your new inbox to confirm the change.'
      );
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  // Save password (auth.updateUser)
  const handleSavePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    if (password !== passwordConfirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setSavingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        toast.error(error.message || 'Failed to update password');
        return;
      }
      setPassword('');
      setPasswordConfirm('');
      toast.success('Password updated');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  // Open Stripe billing portal
  const handleManageBilling = async () => {
    const customerId = profile?.stripe_customer_id;
    if (!customerId) {
      toast.error('No FamousPay customer on file yet — start a subscription first.');
      return;
    }
    setPortalLoading(true);
    try {
      const returnUrl =
        typeof window !== 'undefined'
          ? `${window.location.origin}/settings`
          : 'https://www.kinscircleapp.com/account';
      const { data, error } = await supabase.functions.invoke('account-management', {
        body: { action: 'billing-portal', customerId, returnUrl },
      });
      if (error || !data?.url) {
        toast.error(
          (data && (data.error as string)) ||
            error?.message ||
            'Could not open billing portal'
        );
        return;
      }
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      toast.error(err?.message || 'Could not open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  // Delete account
  const handleDelete = async () => {
    if (deleteConfirm.trim().toUpperCase() !== 'DELETE') {
      toast.error('Type DELETE to confirm.');
      return;
    }
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke('account-management', {
        body: {
          action: 'delete-account',
          userId: user?.id ?? profile?.id,
          email: user?.email ?? profile?.email,
          customerId: profile?.stripe_customer_id ?? null,
        },
      });
      if (error) {
        toast.error(error.message || 'Account deletion failed');
        setDeleting(false);
        return;
      }
      const warnings: string[] = (data?.warnings as string[]) || [];
      if (warnings.length > 0) {
        console.warn('[Account] delete warnings', warnings);
      }
      toast.success('Account deleted. Signing you out…');
      setDeleteOpen(false);
      await signOut();
      window.location.replace('/login');
    } catch (err: any) {
      toast.error(err?.message || 'Account deletion failed');
    } finally {
      setDeleting(false);
    }
  };

  // Owner/admin only: download a JSON snapshot of the family tree.
  // Pure read-only export — does not change any data.
  const handleExportBackup = async () => {
    if (!user?.id) {
      toast.error('You must be signed in to export a backup.');
      return;
    }
    if (!isOwner && !isAdmin) {
      // UI also hides the button, but defend the handler anyway.
      toast.error('Only owners and admins can export the family tree backup.');
      return;
    }
    setExporting(true);
    try {
      const res = await exportFamilyTreeBackup({
        userId: user.id,
        email: user.email ?? profile?.email ?? null,
      });
      if (!res.ok) {
        toast.error(res.error || 'Backup export failed');
        return;
      }
      toast.success(`Backup downloaded${res.filename ? ` (${res.filename})` : ''}`);
    } catch (err: any) {
      toast.error(err?.message || 'Backup export failed');
    } finally {
      setExporting(false);
    }
  };


  // Loading guard
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-fuchsia-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-3">Sign in required</h1>
        <p className="text-slate-600 mb-6">You need to be logged in to view account settings.</p>
        <Button
          onClick={() => navigate('/login', { replace: true })}
          className="rounded-full"
        >
          Back to sign in
        </Button>
      </div>
    );
  }

  const roleLabel = isOwner ? 'Owner' : isAdmin ? 'Admin' : 'Member';
  const subStatusLabel = sub?.status
    ? sub.status.charAt(0).toUpperCase() + sub.status.slice(1).replace(/_/g, ' ')
    : null;
  // Resolve the cycle-aware status display ONCE, here. The JSX below uses
  // the resulting `display.mode` to decide whether to render "Trial ends
  // in X days" (monthly trial only) or "Renews on [date]" — never both.
  const display = resolveBillingDisplay(sub);


  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <button
            onClick={() => navigate('/app')}
            aria-label="Return to KinsCircle"
          >
            <Logo />
          </button>
          <Button
            variant="outline"
            onClick={() => navigate('/app')}
            className="rounded-full gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('Back')}
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Account settings')}</h1>
          <p className="text-slate-600 mt-1">
            {t('Manage your profile, billing, and security.')}
          </p>
        </div>

        <section aria-labelledby="membership-family-access-heading">
          <div className="mb-3">
            <h2
              id="membership-family-access-heading"
              className="text-2xl font-bold text-slate-900"
            >
              Membership &amp; Family Access
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Manage invitations, sponsored seats, family-profile counters,
              membership, billing, and KinsSpace.
            </p>
          </div>

          <MembershipOperationsCenter
            userId={user?.id ?? ''}
            profileClaimed={profileClaimed}
            onInviteFamily={() => {
              setInviteMode('FAMILY_PROFILE');
              setInviteOpen(true);
            }}
            onSponsorFamily={() => {
              setInviteMode('SPONSORED_SEAT');
              setInviteOpen(true);
            }}
          />
        </section>


        {/* Plan + Role overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-fuchsia-600" />
              Plan &amp; role
            </CardTitle>
            <CardDescription>Your current access level on KinsCircle.</CardDescription>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 p-4 bg-slate-50/50">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Plan</div>
              <div className="flex items-center gap-2">
                {plan !== 'free' && <Crown className="w-4 h-4 text-amber-500" />}
                <span className="font-semibold text-slate-900">{planLabel(plan)}</span>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-4 bg-slate-50/50">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Role</div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-fuchsia-600" />
                <span className="font-semibold text-slate-900">{roleLabel}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Language preference — applies to ALL UI text across the app.
            Stored in localStorage (always) AND on the user's profile so
            the choice follows them across devices. */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-fuchsia-600" />
              {t('Language')}
            </CardTitle>
            <CardDescription>{t('Choose the language used across the app.')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-w-xs">
              <Label htmlFor="acct-language" className="sr-only">{t('Language')}</Label>
              <LanguageSelector id="acct-language" hideIcon triggerClassName="w-full rounded-full" />
            </div>
          </CardContent>
        </Card>

        {/* Profile (name + family name) */}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-fuchsia-600" />
              Profile
            </CardTitle>
            <CardDescription>Your display name and family name.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveProfile} className="space-y-4 max-w-xl">
              <div>
                <Label htmlFor="acct-name">Your name</Label>
                <Input
                  id="acct-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="acct-family" className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  Family name
                </Label>
                <Input
                  id="acct-family"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  placeholder="The Smith Family"
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                disabled={savingProfile}
                className="rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white"
              >
                {savingProfile ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save profile
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Email */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-fuchsia-600" />
              Email address
            </CardTitle>
            <CardDescription>
              Used for sign-in and notifications. Changing it will send a confirmation
              email to the new address.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveEmail} className="space-y-4 max-w-xl">
              <div>
                <Label htmlFor="acct-email">Email</Label>
                <Input
                  id="acct-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                disabled={savingEmail}
                variant="outline"
                className="rounded-full"
              >
                {savingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Updating…
                  </>
                ) : (
                  'Update email'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Email notification preferences — additive, no schema changes,
            reuses the existing send-* edge function pipeline. Defaults:
            weekly digest, important updates, birthday reminders, and
            memory activity are all on. */}
        <EmailPreferences
          userId={user?.id ?? profile?.id ?? null}
          userEmail={user?.email ?? profile?.email ?? null}
          userName={profile?.name ?? null}
        />

        {/* Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-fuchsia-600" />
              Password
            </CardTitle>
            <CardDescription>Change the password used to sign in.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSavePassword} className="space-y-4 max-w-xl">
              <div>
                <Label htmlFor="acct-pwd">New password</Label>
                <Input
                  id="acct-pwd"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="acct-pwd2">Confirm new password</Label>
                <Input
                  id="acct-pwd2"
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  autoComplete="new-password"
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                disabled={savingPassword}
                variant="outline"
                className="rounded-full"
              >
                {savingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Updating…
                  </>
                ) : (
                  'Update password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Billing / subscription */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-fuchsia-600" />
              Billing &amp; subscription
            </CardTitle>
            <CardDescription>
              Powered by FamousPay. Manage payment method, invoices, and cancellation
              from the secure customer portal.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {subLoading ? (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading subscription…
              </div>
            ) : sub ? (
              <div className="rounded-lg border border-slate-200 p-4 bg-slate-50/50 grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                    Status
                  </div>
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        sub.status === 'active' || sub.status === 'trialing'
                          ? 'bg-emerald-500'
                          : sub.status === 'past_due' || sub.status === 'unpaid'
                          ? 'bg-amber-500'
                          : 'bg-slate-400'
                      }`}
                    />
                    {subStatusLabel}
                    {sub.cancel_at_period_end && (
                      <span className="text-xs font-normal text-amber-700 ml-1">
                        (cancels at period end)
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                    Price
                  </div>
                  <div className="font-semibold text-slate-900">
                    {fmtMoney(sub.amount, sub.currency)}
                    {sub.interval ? ` / ${sub.interval}` : ''}
                  </div>
                </div>
                {/*
                  Cycle-aware billing line. Strict separation:
                    • monthly + trialing → "Trial ends in X days"
                    • cancelling          → "Ends on [date]"
                    • everything else     → "Renews on [date]"
                  We never show "Trial ends" and "Renews on" together on the
                  same account — only one mode is rendered.
                */}
                {display && display.mode !== 'inactive' && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                      {display.mode === 'trial'
                        ? 'Trial'
                        : display.mode === 'ends'
                        ? 'Ends on'
                        : 'Renews on'}
                    </div>
                    <div className="font-semibold text-slate-900">
                      {display.mode === 'trial'
                        ? display.primary
                        : fmtDate(sub.current_period_end)}
                    </div>
                    {display.secondary && (
                      <div className="text-xs text-slate-500 mt-1">
                        {display.secondary}
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                    Subscription ID
                  </div>
                  <div className="font-mono text-xs text-slate-600 break-all">
                    {sub.id}
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-600">
                {profile?.stripe_customer_id
                  ? 'No active subscription on file.'
                  : 'You are on the Free plan — no subscription yet.'}
              </div>
            )}



            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleManageBilling}
                disabled={portalLoading || !profile?.stripe_customer_id}
                className="rounded-full bg-slate-900 hover:bg-slate-800 text-white gap-2"
              >
                {portalLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4" />
                )}
                Manage billing
              </Button>
              {!profile?.stripe_customer_id && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="rounded-full"
                >
                  See plans
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Privacy & Safety + Terms */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-fuchsia-600" />
              Privacy, Safety &amp; Terms
            </CardTitle>
            <CardDescription>
              Learn how KinsCircle protects your family data, and review the rules that
              every member agrees to.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/privacy')}
                className="rounded-full gap-1.5"
              >
                <ShieldCheck className="w-4 h-4" />
                Open Privacy &amp; Safety
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/terms')}
                className="rounded-full gap-1.5"
              >
                <ScrollText className="w-4 h-4" />
                Open Terms of Service
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Admin-only: System Health panel link. Hidden for regular users so
            their experience is unchanged. */}
        {(isOwner || isAdmin) && (
          <Card className="border-fuchsia-200 bg-gradient-to-br from-fuchsia-50/40 to-pink-50/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-fuchsia-600" />
                System Health
                <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-100 border border-fuchsia-200 text-fuchsia-700 text-[10px] font-semibold uppercase tracking-wide">
                  <ShieldCheck className="w-3 h-3" />
                  Admin
                </span>
              </CardTitle>
              <CardDescription>
                Run safe diagnostics on every core feature — auth, tree, uploads, email,
                birthdays, layouts — and see confirmation-gated repair options.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate('/admin/health')}
                className="rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white gap-1.5"
              >
                <Activity className="w-4 h-4" />
                Open System Health
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Admin-only: Paid-status / FamousPay sync diagnostic.
            Surfaces a clear mapping between the profile row and the Stripe
            customer/subscription so an admin can spot when a paid charge
            never wrote a customer ID onto the profile (the exact failure
            mode that caused the May 3, 2026 owner-account sync issue).
            Read-only — never mutates billing or auth. */}
        {(isOwner || isAdmin) && (() => {
          const hasCustomer = !!profile?.stripe_customer_id;
          const hasSubObj = !!sub;
          const planIsPaid = plan !== 'free';
          // Trouble cases:
          //  • paid plan but no customer on file
          //  • customer on file but no subscription returned from FamousPay
          //  • free plan but a customer is on file with an active sub
          const trouble =
            (planIsPaid && !hasCustomer) ||
            (hasCustomer && !subLoading && !hasSubObj) ||
            (!planIsPaid && hasSubObj && (sub?.status === 'active' || sub?.status === 'trialing'));
          return (
            <Card
              className={
                trouble
                  ? 'border-amber-300 bg-gradient-to-br from-amber-50/60 to-yellow-50/40'
                  : 'border-sky-200 bg-gradient-to-br from-sky-50/40 to-cyan-50/40'
              }
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {trouble ? (
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  ) : (
                    <ShieldCheck className="w-5 h-5 text-sky-600" />
                  )}
                  Paid-status sync
                  <span
                    className={
                      'ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ' +
                      (trouble
                        ? 'bg-amber-100 border-amber-200 text-amber-800'
                        : 'bg-sky-100 border-sky-200 text-sky-700')
                    }
                  >
                    <ShieldCheck className="w-3 h-3" />
                    Admin
                  </span>
                </CardTitle>
                <CardDescription>
                  Read-only diagnostic that maps this profile to its FamousPay
                  customer and active subscription. If a paid charge ever
                  fails to attach to the profile, the mismatch shows up here.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                      Profile plan
                    </div>
                    <div className="font-semibold text-slate-900">{planLabel(plan)}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Role: <span className="font-medium text-slate-700">{roleLabel}</span>
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                      FamousPay customer
                    </div>
                    <div className="font-mono text-xs break-all text-slate-700">
                      {profile?.stripe_customer_id || '— none on file —'}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                      FamousPay subscription
                    </div>
                    <div className="font-mono text-xs break-all text-slate-700">
                      {subLoading
                        ? 'Looking up…'
                        : sub?.id
                        ? sub.id
                        : '— none returned by gateway —'}
                    </div>
                    {sub?.status && (
                      <div className="text-xs text-slate-500 mt-0.5">
                        Status: <span className="font-medium text-slate-700">{sub.status}</span>
                      </div>
                    )}
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                      Profile user id
                    </div>
                    <div className="font-mono text-xs break-all text-slate-700">
                      {profile?.id || user?.id || '—'}
                    </div>
                  </div>
                </div>
                {trouble && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <div className="font-semibold mb-1 flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4" />
                      Mapping looks off
                    </div>
                    <ul className="list-disc list-inside space-y-0.5 text-amber-900/90">
                      {planIsPaid && !hasCustomer && (
                        <li>
                          Profile is on a paid plan ({planLabel(plan)}) but no
                          stripe_customer_id is attached. A previous charge may
                          have failed to write the customer onto the profile.
                        </li>
                      )}
                      {hasCustomer && !subLoading && !hasSubObj && (
                        <li>
                          A FamousPay customer is attached but the gateway did
                          not return any subscription. Check the customer in
                          the Stripe dashboard.
                        </li>
                      )}
                      {!planIsPaid && hasSubObj && (sub?.status === 'active' || sub?.status === 'trialing') && (
                        <li>
                          An active FamousPay subscription exists but the
                          profile is still on Free. The plan column needs to
                          be promoted to match.
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}


        {/* Owner/admin only: Family Tree Backup export. Hidden from
            regular members entirely. Pure read-only — never mutates
            the tree, billing, auth, uploads, or schema. */}
        {(isOwner || isAdmin) && (
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-teal-50/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-emerald-600" />
                Family Tree Backup
                <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-700 text-[10px] font-semibold uppercase tracking-wide">
                  <ShieldCheck className="w-3 h-3" />
                  Admin
                </span>
              </CardTitle>
              <CardDescription>
                Download a backup before major updates.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">
                Exports every visible family member, relationships
                (parent_ids, spouse_id, relationship overrides), birthdays,
                notes, memory counts, and timestamps into a single JSON file
                stamped with the export date and project build marker.
              </p>
              <Button
                onClick={handleExportBackup}
                disabled={exporting}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Preparing backup…
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export Family Tree Backup
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Pre-launch Data Protection panel — admin/owner only.
            Provides full backup (all 6 critical tables + storage manifest)
            and integrity check via the new data-backup-export and
            data-integrity-check edge functions. Read-only, never mutates. */}
        {(isOwner || isAdmin) && (
          <DataProtectionPanel
            userId={user?.id ?? null}
            isAdmin={isOwner || isAdmin}
          />
        )}


        {/* Danger zone */}
        <Card className="border-rose-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" />
              Danger zone
            </CardTitle>
            <CardDescription>
              Permanently remove your account, profile, and any active subscription.
              This action cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Production stability charter §7.7 — Owner/admin safety:
                Remind owners and admins to export a Family Tree Backup
                before any major settings change (account deletion is the
                most destructive). The Family Tree Backup card is rendered
                directly above this one. Read-only reminder; never blocks
                the destructive action. */}
            {(isOwner || isAdmin) && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">
                    Export a Family Tree Backup first
                  </div>
                  <div className="text-amber-900/90 text-xs mt-0.5">
                    Before deleting your account or making major settings
                    changes, scroll up and tap{' '}
                    <span className="font-semibold">
                      Export Family Tree Backup
                    </span>{' '}
                    so you keep a copy of every member, relationship, and
                    memory. KinsCircle preserves family data permanently
                    (see Privacy &amp; Safety) — but a personal backup is
                    always recommended.
                  </div>
                </div>
              </div>
            )}
            <Button
              variant="destructive"
              onClick={() => {
                setDeleteConfirm('');
                setDeleteOpen(true);
              }}
              className="rounded-full"
            >
              Delete account
            </Button>
          </CardContent>
        </Card>
      </main>

      <InviteModal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        initialInviteType={inviteMode}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" />
              Delete your account?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This will cancel any active FamousPay subscription, remove your profile,
                and sign you out permanently.
              </span>
              <span className="block font-medium text-slate-700">
                Type <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">DELETE</span>{' '}
                below to confirm.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder="DELETE"
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting || deleteConfirm.trim().toUpperCase() !== 'DELETE'}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting…
                </>
              ) : (
                'Delete forever'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Account;
